/**
 * Sentry crash + error observability.
 *
 * Workstream C launch decision (locked, 2026-05-17): we need third-party
 * crash/error visibility before TestFlight/App Store submission so the team
 * does not have to wait on Xcode Organiser for the first wave of trainers.
 *
 * Gating contract — read this before changing anything:
 *
 *   - Sentry is enabled iff `VITE_SENTRY_DSN` is set at build time.
 *   - When the DSN is absent (any local/web build that doesn't ship to the
 *     store), `initSentry()` is a no-op. The app must function identically.
 *   - We never call `setUser({ email, ... })`. Accounts join via the numeric
 *     `accountId` only — no email, no trainer name, no friend code.
 *   - `sendDefaultPii: false` and `beforeSend` strip URL query strings,
 *     request headers, and any free-text breadcrumbs that could carry
 *     trainer-typed data. Anything we can't be sure about gets dropped.
 *   - This is iOS-first. We don't run Replay (privacy + bundle size) and we
 *     don't run BrowserTracing yet — the marginal data isn't worth the
 *     fetch-instrumentation surface on Capacitor right now.
 *
 * Privacy label + privacy policy updates required before TestFlight:
 *   - Privacy nutrition: declare "Crash data" and "Performance data" as
 *     collected, not linked to identity, used for App Functionality.
 *   - Privacy policy at nexusdex.ai/privacy: list Sentry as a third-party
 *     processor under "Crash and error diagnostics" with a link to
 *     https://sentry.io/privacy/.
 */
import * as Sentry from "@sentry/react";

let initialized = false;

function readEnv(key: string): string | undefined {
  // import.meta.env is the canonical Vite env source. Guard for the rare
  // SSR-ish test harness where it might be undefined.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const env = (import.meta as any)?.env;
  const val = env?.[key];
  return typeof val === "string" && val.length > 0 ? val : undefined;
}

/**
 * Initialise Sentry. Idempotent; safe to call before React renders. Returns
 * true when Sentry was actually initialised so callers can branch on whether
 * the error boundary should be the Sentry-wrapped flavour.
 */
export function initSentry(): boolean {
  if (initialized) return true;
  const dsn = readEnv("VITE_SENTRY_DSN");
  if (!dsn) return false;

  const release = readEnv("VITE_SENTRY_RELEASE") || readEnv("VITE_APP_VERSION");
  const environment =
    readEnv("VITE_SENTRY_ENVIRONMENT") ||
    (readEnv("MODE") ?? (readEnv("PROD") ? "production" : "development"));

  Sentry.init({
    dsn,
    release,
    environment,
    // Keep the SDK minimal until we have a privacy-label commitment for the
    // larger feature set. No Replay, no Tracing, no profiling.
    integrations: [],
    // Performance sampling stays off; flip to a small number after launch.
    tracesSampleRate: 0,
    // Hard guarantee: never send the email / IP / cookies that the SDK
    // would otherwise attach to events.
    sendDefaultPii: false,
    // Drop noisy expected errors (network blips on Capacitor, abort from
    // ReactQuery cancellation) before they hit the project quota.
    ignoreErrors: [
      "AbortError",
      "Network request failed",
      "Load failed",
      "TypeError: Failed to fetch",
      // Workstream B insights endpoint sometimes 401s mid-handshake before
      // the auth token rehydrates. The client retries; the error is noise.
      /401.*\/api\/(insights|user\/state)/,
    ],
    beforeBreadcrumb(breadcrumb) {
      // The default breadcrumbs include the full URL including query
      // strings. We mounted Nex companion messages and trade strings in
      // hash params during early dev — strip the query/hash defensively
      // even though current routes don't carry PII.
      if (breadcrumb.data && typeof breadcrumb.data === "object") {
        const data = { ...breadcrumb.data } as Record<string, unknown>;
        for (const key of ["url", "to", "from"]) {
          const val = data[key];
          if (typeof val === "string") {
            data[key] = val.replace(/[?#].*$/, "");
          }
        }
        breadcrumb.data = data;
      }
      return breadcrumb;
    },
    beforeSend(event) {
      // Strip request body, cookies and headers if the SDK ever attaches
      // them via a future integration.
      if (event.request) {
        delete event.request.cookies;
        delete event.request.headers;
        delete event.request.data;
        if (event.request.url) {
          event.request.url = event.request.url.replace(/[?#].*$/, "");
        }
        if (event.request.query_string) {
          delete event.request.query_string;
        }
      }
      // Never let an integration leak the user's email or IP.
      if (event.user) {
        delete event.user.email;
        delete event.user.ip_address;
        delete event.user.username;
      }
      return event;
    },
  });

  initialized = true;
  return true;
}

/**
 * Attach the signed-in account id as a Sentry user. Numeric id only — never
 * email, username, trainer name, or friend code. Called by AuthProvider when
 * the auth state changes so the crash reports carry the minimum context the
 * team needs to triage launch-day issues.
 */
export function setSentryAccount(accountId: number | null): void {
  if (!initialized) return;
  if (accountId == null) {
    Sentry.setUser(null);
    return;
  }
  Sentry.setUser({ id: String(accountId) });
}

/** Re-export the Sentry ErrorBoundary so callers don't need to import the SDK directly. */
export { Sentry };
